import useStore from 'state/knovStore'
import api, { SendBsvResult } from 'api/api'
import { debounce } from 'lodash'
import queryClient from 'api/queryClient'
import { WALLET_DETAILS_QUERY_KEY, WalletDetails } from 'api/useWalletDetails'
import {
    signTransaction,
    isValidWIF,
    getKeyInfo,
    generateRandomKey,
    splitKey,
    recombineShares,
    verifyShares,
    storeClientShare,
    getClientShare,
    clearClientShare,
    listAllClientShares,
    type UnsignedTransactionTemplate,
    type SigningResult,
    type ShamirShares,
    type ClientShareRecord,
} from 'lib/bsv-signing'
import * as bsvTxBuilder from 'lib/bsv-tx-builder'
import {
    NO_LOCAL_SIGNING_KEY_MESSAGE,
    resolveClientSigningDeviceId,
} from 'wallets/clientSigningDevice'
import { offerWalletPasskeyRestore } from 'lib/walletPasskeyRestoreOffer'
import { treechatSignsWithoutLocalKey } from 'lib/clientSigningFeature'

// Re-export BSV signing utilities for self-custody wallet support
export * from 'lib/bsv-signing'

// ============================================================================
// Console Testing Interface for BSV Signing
// ============================================================================
// Exposes signing functions on window.bsvSigning for development testing.
// Usage from browser console:
//
//   // 1. Validate a WIF key
//   bsvSigning.isValidWIF('L1...')
//
//   // 2. Get key info (public key + address)
//   bsvSigning.getKeyInfo('L1...')
//
//   // 3. Sign a transaction (see bsvSigning.help() for full example)
//   await bsvSigning.signTransaction(template, wif)
//
//   // 4. Run a full test with a mock transaction
//   await bsvSigning.testSign('L1...')
//
// ============================================================================

interface BsvSigningTestInterface {
    signTransaction: typeof signTransaction
    isValidWIF: typeof isValidWIF
    getKeyInfo: typeof getKeyInfo
    generateRandomKey: typeof generateRandomKey
    splitKey: typeof splitKey
    recombineShares: typeof recombineShares
    verifyShares: typeof verifyShares
    storeClientShare: typeof storeClientShare
    getClientShare: typeof getClientShare
    clearClientShare: typeof clearClientShare
    listAllClientShares: typeof listAllClientShares
    testSign: (privateKeyWIF?: string) => Promise<SigningResult>
    testShamir: () => { shares: ShamirShares; recombinedWif: string; verified: boolean }
    testStorage: (userId?: string) => Promise<{
        stored: ClientShareRecord
        retrieved: ClientShareRecord | null
        verified: boolean
    }>
    testFullFlow: (userId?: string) => Promise<{
        success: boolean
        deviceId: string
        address: string
        signResult: SigningResult
    }>
    help: () => void
}

const bsvSigningTestInterface: BsvSigningTestInterface = {
    signTransaction,
    isValidWIF,
    getKeyInfo,
    generateRandomKey,
    splitKey,
    recombineShares,
    verifyShares,
    storeClientShare,
    getClientShare,
    clearClientShare,
    listAllClientShares,

    /**
     * Test signing with a mock transaction template.
     * This creates a fake transaction to verify the signing flow works.
     * The resulting transaction won't be valid on-chain (fake UTXO).
     * If no WIF is provided, generates a random key for testing.
     */
    testSign: async (privateKeyWIF?: string): Promise<SigningResult> => {
        let wif: string
        let keyInfo: { publicKey: string; address: string } | null

        if (privateKeyWIF) {
            keyInfo = getKeyInfo(privateKeyWIF)
            if (!keyInfo) {
                return { success: false, error: 'Invalid WIF provided' }
            }
            wif = privateKeyWIF
        } else {
            console.log('>>> No WIF provided, generating random key for testing...')
            const randomKey = generateRandomKey()
            wif = randomKey.wif
            keyInfo = { publicKey: randomKey.publicKey, address: randomKey.address }
        }

        console.log('>>> BSV Signing Test')
        console.log('>>> Public Key:', keyInfo.publicKey)
        console.log('>>> Address:', keyInfo.address)

        // Create a mock P2PKH locking script for the address
        // P2PKH: OP_DUP OP_HASH160 <pubkeyhash> OP_EQUALVERIFY OP_CHECKSIG
        // We'll use a placeholder since we need the actual pubkeyhash
        const mockTemplate: UnsignedTransactionTemplate = {
            version: 1,
            inputs: [
                {
                    txid: 'a'.repeat(64), // Mock txid (32 bytes hex)
                    vout: 0,
                    satoshis: 10000,
                    // Standard P2PKH locking script (this is a placeholder - real one needs pubkeyhash)
                    scriptPubKey: '76a914' + '0'.repeat(40) + '88ac', // OP_DUP OP_HASH160 <20 bytes> OP_EQUALVERIFY OP_CHECKSIG
                },
            ],
            outputs: [
                {
                    script: '76a914' + '1'.repeat(40) + '88ac', // Send to another address
                    satoshis: 9500,
                },
            ],
            lockTime: 0,
        }

        console.log('>>> Signing mock transaction...')
        const startTime = performance.now()
        const result = await signTransaction(mockTemplate, wif)
        const elapsed = performance.now() - startTime

        if (result.success) {
            console.log(`>>> Signing succeeded in ${elapsed.toFixed(2)}ms`)
            console.log('>>> Raw TX length:', result.rawTx?.length, 'chars')
            console.log('>>> Raw TX (first 100 chars):', result.rawTx?.substring(0, 100) + '...')
        } else {
            console.error('>>> Signing failed:', result.error)
        }

        return result
    },

    /**
     * Test the full Shamir secret sharing flow:
     * 1. Generate a random key
     * 2. Split it into 2-of-2 shares
     * 3. Recombine the shares
     * 4. Verify the recombined key matches the original
     */
    testShamir: () => {
        console.log('>>> Shamir Secret Sharing Test (2-of-2)')
        console.log('')

        // Generate a random key
        const startGen = performance.now()
        const key = generateRandomKey()
        const genTime = performance.now() - startGen
        console.log(`>>> 1. Generated random key in ${genTime.toFixed(2)}ms`)
        console.log('>>>    WIF:', key.wif)
        console.log('>>>    Address:', key.address)
        console.log('')

        // Split into shares
        const startSplit = performance.now()
        const shares = splitKey(key.wif)
        const splitTime = performance.now() - startSplit
        console.log(`>>> 2. Split key into 2-of-2 shares in ${splitTime.toFixed(2)}ms`)
        console.log('>>>    Treechat share:', shares.treechatShare)
        console.log('>>>    Client share:', shares.clientShare)
        console.log('')

        // Recombine shares
        const startRecombine = performance.now()
        const recombinedWif = recombineShares(shares.treechatShare, shares.clientShare)
        const recombineTime = performance.now() - startRecombine
        console.log(`>>> 3. Recombined shares in ${recombineTime.toFixed(2)}ms`)
        console.log('>>>    Recombined WIF:', recombinedWif)
        console.log('')

        // Verify
        const keysMatch = recombinedWif === key.wif
        const verified = verifyShares(shares.treechatShare, shares.clientShare, key.address)
        console.log('>>> 4. Verification:')
        console.log('>>>    Keys match:', keysMatch ? '✓ YES' : '✗ NO')
        console.log('>>>    Address verified:', verified ? '✓ YES' : '✗ NO')
        console.log('')

        if (keysMatch && verified) {
            console.log('>>> ✓ Shamir secret sharing test PASSED')
        } else {
            console.error('>>> ✗ Shamir secret sharing test FAILED')
        }

        return {
            shares,
            recombinedWif: recombinedWif || '',
            verified: keysMatch && verified,
        }
    },

    /**
     * Test the full IndexedDB storage flow:
     * 1. Generate a random key and split it
     * 2. Store the client share in IndexedDB
     * 3. Retrieve it back
     * 4. Verify it matches and can recombine
     */
    testStorage: async (userId?: string) => {
        const testUserId = userId ?? `test-user-${Date.now()}`
        console.log('>>> IndexedDB Storage Test')
        console.log('>>>    User ID:', testUserId)
        console.log('')

        // Generate key and split
        const key = generateRandomKey()
        const shares = splitKey(key.wif)
        console.log('>>> 1. Generated key and split into shares')
        console.log('>>>    Address:', shares.address)
        console.log('>>>    Client share:', shares.clientShare.substring(0, 40) + '...')
        console.log('')

        // Store in IndexedDB
        const startStore = performance.now()
        const stored = await storeClientShare(testUserId, shares.clientShare, shares.address)
        const storeTime = performance.now() - startStore
        console.log(`>>> 2. Stored client share in IndexedDB in ${storeTime.toFixed(2)}ms`)
        console.log('>>>    Device ID:', stored.deviceId)
        console.log('>>>    Created at:', new Date(stored.createdAt).toISOString())
        console.log('')

        // Retrieve from IndexedDB
        const startGet = performance.now()
        const retrieved = await getClientShare(testUserId)
        const getTime = performance.now() - startGet
        console.log(`>>> 3. Retrieved client share from IndexedDB in ${getTime.toFixed(2)}ms`)
        console.log('>>>    Device ID:', retrieved?.deviceId)
        console.log(
            '>>>    Share matches:',
            retrieved?.clientShare === shares.clientShare ? '✓ YES' : '✗ NO',
        )
        console.log('')

        // Verify we can recombine
        const canRecombine = retrieved
            ? verifyShares(shares.treechatShare, retrieved.clientShare, shares.address)
            : false
        console.log('>>> 4. Verification:')
        console.log('>>>    Can recombine with server share:', canRecombine ? '✓ YES' : '✗ NO')
        console.log('')

        // Clean up test data (unless a real userId was provided)
        if (!userId) {
            await clearClientShare(testUserId)
            console.log('>>> 5. Cleaned up test data')
            console.log('')
        }

        const verified = retrieved?.clientShare === shares.clientShare && canRecombine
        if (verified) {
            console.log('>>> ✓ IndexedDB storage test PASSED')
        } else {
            console.error('>>> ✗ IndexedDB storage test FAILED')
        }

        return {
            stored,
            retrieved,
            verified,
        }
    },

    /**
     * Test the FULL end-to-end self-custody flow:
     * 1. Generate a key
     * 2. Split into shares
     * 3. Store client share in IndexedDB (get deviceId)
     * 4. Store server share via API (stub)
     * 5. Retrieve both shares
     * 6. Recombine into full key
     * 7. Sign a mock transaction
     * 8. Discard the key
     *
     * This tests the complete flow that will happen in production.
     */
    testFullFlow: async (userId?: string) => {
        const testUserId = userId ?? `test-user-${Date.now()}`
        console.log('>>> FULL SELF-CUSTODY FLOW TEST')
        console.log('>>> User ID:', testUserId)
        console.log('')

        // Step 1: Generate a key
        console.log('>>> 1. Generating private key...')
        const key = generateRandomKey()
        console.log('>>>    Address:', key.address)
        console.log('>>>    (Key exists only in memory)')
        console.log('')

        // Step 2: Split into shares
        console.log('>>> 2. Splitting key into 2-of-2 Shamir shares...')
        const shares = splitKey(key.wif)
        console.log(
            '>>>    Treechat share (first 40 chars):',
            shares.treechatShare.substring(0, 40) + '...',
        )
        console.log(
            '>>>    Client share (first 40 chars):',
            shares.clientShare.substring(0, 40) + '...',
        )
        console.log('')

        // Step 3: Store client share in IndexedDB
        console.log('>>> 3. Storing client share in IndexedDB...')
        const clientRecord = await storeClientShare(testUserId, shares.clientShare, shares.address)
        console.log('>>>    Device ID:', clientRecord.deviceId)
        console.log('')

        // Step 4: Explain current limitation
        console.log('>>> 4. Server share setup API is not implemented in this branch.')
        console.log(
            '>>>    Full end-to-end flow requires a pre-seeded server share for this device.',
        )
        console.log('>>>    This console test now verifies the client-side pieces only.')
        console.log('')

        // Step 5: Clear the original key from memory (simulate real flow)
        console.log('>>> 5. Discarding original key from memory...')
        console.log('>>>    (In production, key would be zeroized here)')
        console.log('')

        // Step 6: Retrieve both shares (simulating later signing request)
        console.log('>>> 6. Retrieving shares for signing...')
        const retrievedClient = await getClientShare(testUserId)
        const retrievedServer = await api.getBsvServerShare(testUserId, clientRecord.deviceId)

        if (!retrievedClient || !retrievedServer) {
            console.error('>>> ✗ Failed to retrieve both shares')
            console.error(
                '>>>    Client share exists, but server share must be created separately.',
            )
            return {
                success: false,
                deviceId: clientRecord.deviceId,
                address: shares.address,
                signResult: {
                    success: false,
                    error: 'Failed to retrieve both shares. Server share setup is still manual.',
                },
            }
        }
        console.log('>>>    Client share retrieved from IndexedDB: ✓')
        console.log('>>>    Server share retrieved from API: ✓')
        console.log('')

        // Step 7: Recombine shares
        console.log('>>> 7. Recombining shares...')
        const startRecombine = performance.now()
        const recoveredWif = recombineShares(
            retrievedServer.treechatShare,
            retrievedClient.clientShare,
        )
        const recombineTime = performance.now() - startRecombine

        if (!recoveredWif) {
            console.error('>>> ✗ Failed to recombine shares')
            return {
                success: false,
                deviceId: clientRecord.deviceId,
                address: shares.address,
                signResult: { success: false, error: 'Failed to recombine shares' },
            }
        }

        const keysMatch = recoveredWif === key.wif
        console.log(`>>>    Recombined in ${recombineTime.toFixed(2)}ms`)
        console.log('>>>    Keys match original:', keysMatch ? '✓ YES' : '✗ NO')
        console.log('')

        // Step 8: Sign a mock transaction
        console.log('>>> 8. Signing mock transaction...')
        const mockTemplate: UnsignedTransactionTemplate = {
            version: 1,
            inputs: [
                {
                    txid: 'a'.repeat(64),
                    vout: 0,
                    satoshis: 10000,
                    scriptPubKey: '76a914' + '0'.repeat(40) + '88ac',
                },
            ],
            outputs: [
                {
                    script: '76a914' + '1'.repeat(40) + '88ac',
                    satoshis: 9500,
                },
            ],
            lockTime: 0,
        }

        const startSign = performance.now()
        const signResult = await signTransaction(mockTemplate, recoveredWif)
        const signTime = performance.now() - startSign

        if (signResult.success) {
            console.log(`>>>    Signed in ${signTime.toFixed(2)}ms`)
            console.log('>>>    Raw TX length:', signResult.rawTx?.length, 'chars')
        } else {
            console.error('>>>    Signing failed:', signResult.error)
        }
        console.log('')

        // Step 9: Discard the recovered key
        console.log('>>> 9. Discarding recovered key from memory...')
        console.log('>>>    (In production, would zeroize the WIF here)')
        console.log('')

        // Cleanup test data (unless real userId provided)
        if (!userId) {
            console.log('>>> 10. Cleaning up test data...')
            await clearClientShare(testUserId)
            console.log('')
        }

        const success = keysMatch && signResult.success
        if (success) {
            console.log('>>> ════════════════════════════════════════════════════════')
            console.log('>>> ✓ FULL SELF-CUSTODY FLOW TEST PASSED')
            console.log('>>> ════════════════════════════════════════════════════════')
        } else {
            console.error('>>> ════════════════════════════════════════════════════════')
            console.error('>>> ✗ FULL SELF-CUSTODY FLOW TEST FAILED')
            console.error('>>> ════════════════════════════════════════════════════════')
        }

        return {
            success,
            deviceId: clientRecord.deviceId,
            address: shares.address,
            signResult,
        }
    },

    help: () => {
        console.log(`
╔═══════════════════════════════════════════════════════════════════════════════╗
║                        BSV Client-Side Signing Test                           ║
╠═══════════════════════════════════════════════════════════════════════════════╣
║                                                                               ║
║  KEY GENERATION & INFO                                                        ║
║  ────────────────────                                                         ║
║  generateRandomKey() => { wif, publicKey, address }                           ║
║  isValidWIF(wif) => boolean                                                   ║
║  getKeyInfo(wif) => { publicKey, address } | null                             ║
║                                                                               ║
║  SHAMIR SECRET SHARING (2-of-2)                                               ║
║  ──────────────────────────────                                               ║
║  splitKey(wif) => { treechatShare, clientShare, address, publicKey }          ║
║  recombineShares(treechatShare, clientShare) => wif | null                    ║
║  verifyShares(treechatShare, clientShare, expectedAddress) => boolean         ║
║                                                                               ║
║  INDEXEDDB STORAGE (client share persistence)                                 ║
║  ────────────────────────────────────────────                                 ║
║  storeClientShare(userId, share, address) => Promise<ClientShareRecord>       ║
║  getClientShare(userId) => Promise<ClientShareRecord | null>                  ║
║  clearClientShare(userId) => Promise<boolean>                                 ║
║  listAllClientShares() => Promise<ClientShareRecord[]>                        ║
║                                                                               ║
║  ClientShareRecord: { userId, deviceId, clientShare, address, createdAt }     ║
║                                                                               ║
║  TRANSACTION SIGNING                                                          ║
║  ───────────────────                                                          ║
║  signTransaction(template, wif) => Promise<{ success, rawTx?, error? }>       ║
║                                                                               ║
║  QUICK TESTS                                                                  ║
║  ───────────                                                                  ║
║  testSign(wif?) - Test signing (auto-generates key if no wif)                 ║
║  testShamir() - Test full split/recombine flow                                ║
║  await testStorage(userId?) - Test IndexedDB store/retrieve                   ║
║  await testFullFlow(userId?) - TEST COMPLETE END-TO-END FLOW                  ║
║                                                                               ║
║  RECOMMENDED: Run testFullFlow() to verify the entire self-custody system     ║
║                                                                               ║
║  EXAMPLES                                                                     ║
║  ────────                                                                     ║
║  // Test everything end-to-end (recommended)                                  ║
║  await bsvSigning.testFullFlow()                                              ║
║                                                                               ║
║  // Individual tests                                                          ║
║  bsvSigning.testShamir()                                                      ║
║  await bsvSigning.testSign()                                                  ║
║  await bsvSigning.testStorage()                                               ║
║                                                                               ║
╚═══════════════════════════════════════════════════════════════════════════════╝
        `)
    },
}

// Attach to window for console access
declare global {
    interface Window {
        bsvSigning: BsvSigningTestInterface
        bsvTxBuilder: typeof bsvTxBuilder
    }
}

window.bsvSigning = bsvSigningTestInterface
window.bsvTxBuilder = bsvTxBuilder
console.log('>>> BSV signing test interface loaded. Type bsvSigning.help() for usage.')
console.log('>>> BSV transaction builder loaded. Type bsvTxBuilder.help() for usage.')

export enum BsvWalletType {
    SHUALLET = 'SHUAllet',
    PANDA = 'panda',
    // RELAYX = 'relayx'
}

export function initWallet(walletType: BsvWalletType) {
    switch (walletType) {
        case BsvWalletType.SHUALLET:
            return new SHUAllet()
        case BsvWalletType.PANDA:
            return new PandaWallet()
        default:
            throw new Error('Invalid wallet type')
    }
}

export interface IWalletInterface {
    authenticate(): Promise<void>
    type(): BsvWalletType
    /**
     * asynchronously retrieves the wallet's balance, does not return the balance itself.
     * balance can be retrieved via the useWalletDetails hook
     */
    getBalance(): Promise<void>
    /**
     * asynchronously retrieves the wallet's address, does not return the address itself.
     * address can be retrieved via the useWalletDetails hook
     */
    getAddress(): Promise<void>
    getPaymail(): Promise<string>
    sendBsv(
        address: string,
        amountSats: number,
        additionalPayments?: Array<{ amount: number; addr: string }>,
        toTreechatUsers?: string[],
        paymentId?: string,
        includeReceipt?: boolean,
    ): Promise<SendBsvResult>
    broadcastTx(
        rawTx: string,
        fund?: boolean,
        walletAddress?: string,
        privateKeyWIF?: string,
    ): Promise<string>
}

class WalletBase implements IWalletInterface {
    constructor() {
        // this.authenticate()
    }
    async authenticate(): Promise<void> {
        throw new Error('Method not implemented.')
    }
    type(): BsvWalletType {
        throw new Error('Method not implemented.')
    }
    async getBalance(): Promise<void> {
        throw new Error('Method not implemented.')
    }
    async getAddress(): Promise<void> {
        throw new Error('Method not implemented.')
    }
    async getPaymail(): Promise<string> {
        throw new Error('Method not implemented.')
    }
    async sendBsv(
        address: string,
        amountSats: number,
        additionalPayments?: Array<{ amount: number; addr: string }>,
        toTreechatUsers?: string[],
        paymentId?: string,
        includeReceipt?: boolean,
    ): Promise<SendBsvResult> {
        throw new Error('Method not implemented.')
    }
    async broadcastTx(
        rawTx: string,
        fund?: boolean,
        walletAddress?: string,
        privateKeyWIF?: string,
    ): Promise<string> {
        throw new Error('Method not implemented.')
    }
}

export class SHUAllet extends WalletBase {
    private debouncedEnsureBsvWallet: () => Promise<void>
    private debouncedGetBalance: () => Promise<void>
    private addressRequest: Promise<void> | null

    constructor() {
        super()
        this.debouncedEnsureBsvWallet = debounce(() => api.ensureBsvWallet(), 300)
        this.debouncedGetBalance = debounce(() => api.getBsvBalance(), 300)
        this.addressRequest = null
    }

    type(): BsvWalletType {
        return BsvWalletType.SHUALLET
    }

    async authenticate(): Promise<void> {
        // We go through rails api instead of bsv node service directly so as not to expose keys.
        await this.debouncedEnsureBsvWallet()
        await this.debouncedGetBalance()
        await this.getAddress()
    }

    async getBalance(): Promise<void> {
        await this.debouncedGetBalance()
    }

    async getAddress(): Promise<void> {
        if (!this.addressRequest) {
            this.addressRequest = api
                .getBsvAddress()
                .then(response => {
                    if (!response?.ok || !response?.address_loaded) return

                    queryClient.setQueryData<WalletDetails>(
                        [WALLET_DETAILS_QUERY_KEY, this.type()],
                        oldData => {
                            const seedData = {
                                wallet: oldData?.wallet || this,
                                balance: oldData?.balance || 0,
                                confirmedBalance: oldData?.confirmedBalance,
                                unconfirmedBalance: oldData?.unconfirmedBalance,
                                address: oldData?.address || '',
                                ordinalsAddress: oldData?.ordinalsAddress || '',
                                balanceLoaded: oldData?.balanceLoaded || false,
                                addressLoaded: oldData?.addressLoaded || false,
                            }
                            const address = response.address || seedData.address || ''
                            const ordinalsAddress =
                                response.ordinals_address || seedData.ordinalsAddress || ''

                            return {
                                ...seedData,
                                address,
                                ordinalsAddress,
                                addressLoaded: Boolean(address),
                            } as WalletDetails
                        },
                    )
                })
                .finally(() => {
                    this.addressRequest = null
                })
        }

        await this.addressRequest
    }

    async getPaymail(): Promise<string> {
        // shuallet doesn't actually have paymails, so just return address for now
        return useStore?.getState()?.currentUserBsvAddress
    }

    async sendBsv(
        address: string,
        amountSats: number,
        additionalPayments?: Array<{ amount: number; addr: string }>,
        toTreechatUsers?: string[],
        paymentId?: string,
        includeReceipt?: boolean,
    ): Promise<SendBsvResult> {
        const deviceId = await resolveClientSigningDeviceId({
            userId: gon.currentUser?.id,
            features: gon.currentUser?.features,
            getClientShare,
            serverFallback: treechatSignsWithoutLocalKey(),
        }).catch(async error => {
            if (!(error instanceof Error) || error.message !== NO_LOCAL_SIGNING_KEY_MESSAGE)
                throw error
            // The unlock screen or the one-tap passkey notice restores this browser.
            const restoreOffered = await offerWalletPasskeyRestore({ fromAction: true }).catch(
                () => false,
            )
            throw restoreOffered
                ? new Error('This browser lost its wallet keys. Restore them, then try again.')
                : error
        })

        return api.sendBsv(
            address,
            amountSats,
            additionalPayments,
            toTreechatUsers,
            paymentId,
            deviceId,
            includeReceipt,
        )
    }

    async broadcastTx(rawTx: string, fund: boolean): Promise<string> {
        const res = await api.broadcastBsvTx(rawTx, fund)
        const txid = res?.tx_id
        if (!txid) {
            throw new Error('Failed to broadcast transaction: No transaction ID returned.')
        }
        return txid
    }
}

export class PandaWallet extends WalletBase {
    constructor() {
        super()
    }

    type(): BsvWalletType {
        return BsvWalletType.PANDA
    }

    async authenticate(): Promise<void> {
        const panda = await this._getPanda()
        useStore.getState().set({
            currentUserBsvWalletType: this.type(),
        })
        await this.getBalance()
        await this.getAddress()
    }

    async broadcastTx(
        rawTx: string,
        fund?: boolean,
        walletAddress?: string,
        privateKeyWIF?: string,
    ): Promise<string> {
        // not implemented for panda wallet yet
        const panda = await this._getPanda()
        try {
            // broadcast transaction using panda wallet
            console.log('panda broadcasting:', { rawTx, fund })
            const txId = await panda.broadcast({ rawtx: rawTx, fund })
            console.log('panda broadcasted:', { rawTx, fund })
            return txId
        } catch (error) {
            console.error('Failed to broadcast transaction with Panda Wallet', error, error.stack)
            throw new Error('Failed to broadcast transaction with Panda Wallet')
        }
    }

    // helper function to get panda wallet object if it's ready and connected
    private async _getPanda(): Promise<any> {
        if ('panda' in window && window.panda?.isReady) {
            if (!(await window.panda.isConnected())) {
                await window.panda.connect()
            }
            return window.panda
        } else {
            console.error('Panda Wallet is not available or not ready')
            throw new Error('Panda Wallet is not available or not ready')
        }
    }

    async getBalance(): Promise<void> {
        if (!window.panda) return
        const { satoshis: currentUserBsvBalance } = await window.panda.getBalance()
        queryClient.setQueryData<WalletDetails>(
            [WALLET_DETAILS_QUERY_KEY, this.type()],
            oldData =>
                ({
                    ...oldData,
                    balance: currentUserBsvBalance,
                } as WalletDetails),
        )
    }

    async getAddress(): Promise<void> {
        if (!window.panda) return
        const { identityAddress: currentUserBsvAddress } = await window.panda.getAddresses()
        queryClient.setQueryData<WalletDetails>(
            [WALLET_DETAILS_QUERY_KEY, this.type()],
            oldData =>
                ({
                    ...oldData,
                    address: currentUserBsvAddress,
                } as WalletDetails),
        )
    }

    // placeholders for the methods we're disregarding for now
    async getPaymail(): Promise<string> {
        // not implemented for panda wallet yet
        return Promise.reject('getPaymail not implemented for PandaWallet')
    }

    async sendBsv(
        address: string,
        amountSats: number,
        additionalPayments?: Array<{ amount: number; addr: string }>,
        toTreechatUsers?: string[],
        paymentId?: string,
        includeReceipt?: boolean,
    ): Promise<SendBsvResult> {
        // not implemented for panda wallet yet
        return Promise.reject('sendBsv not implemented for PandaWallet')
    }
}

/**
 * instantiates a wallet based on the provided wallet type.
 * @param walletType The type of the wallet to be instantiated.
 * @returns An instance of IWalletInterface.
 */
export async function instantiateWallet(walletType: BsvWalletType): Promise<IWalletInterface> {
    let wallet: IWalletInterface
    switch (walletType) {
        case BsvWalletType.SHUALLET:
            wallet = new SHUAllet()
            break
        case BsvWalletType.PANDA:
            // Placeholder for Panda wallet instantiation
            // wallet = new PandaWallet();
            break
        default:
            throw new Error('Invalid wallet type')
    }
    // wallet.authenticate()
    return wallet
}
